import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Steps,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import { adjacentCropService } from "../../../services/api";
import type {
  AdjacentCrop,
  CropSpecies,
  CropVariety,
} from "../../../types/adjacent-crop";
import { AdjacentCropMapEditor } from "../../adjacent-crops/components/AdjacentCropMapEditor";

const { TextArea } = Input;

interface AdjacentCropReviewFormProps {
  /** Backwards-compatible name — this component now self-renders a
   *  Modal, so it needs visibility wiring from the parent. */
  visible?: boolean;
  auditId: number;
  adjacentCrop: AdjacentCrop;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ReviewFormValues {
  species: string;
  variety?: string;
  estimated_surface: number;
  comment: string;
}

/**
 * Wizard modal for reviewing an adjacent crop that comes precharged
 * from a previous audit. Same two-step shape as the plot review:
 *   1. Datos    — verify (and tweak) the catalogue info + leave a
 *                 review comment.
 *   2. Mapa     — confirm or redraw the geometry.
 * "Finalizar revisión" saves a new review on the audit and closes
 * the modal. The "Dar de baja" action lives on the row in the
 * AdjacentCropsSection — not inside this modal — so the auditor
 * doesn't even have to open the review when they only need to
 * close the crop.
 */
export const AdjacentCropReviewForm = ({
  visible = true,
  auditId,
  adjacentCrop,
  onSuccess,
  onCancel,
}: AdjacentCropReviewFormProps) => {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<ReviewFormValues>();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(
    adjacentCrop.geom ?? null,
  );
  const [species, setSpecies] = useState<CropSpecies[]>([]);
  const [varieties, setVarieties] = useState<CropVariety[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(
    adjacentCrop.species,
  );

  // Reset everything whenever a different crop is loaded.
  useEffect(() => {
    if (!visible) return;
    setStep(0);
    setGeometry(adjacentCrop.geom ?? null);
    setSelectedSpecies(adjacentCrop.species);
    form.setFieldsValue({
      species: adjacentCrop.species,
      variety: adjacentCrop.variety,
      estimated_surface: adjacentCrop.total_estimated_surface,
      comment: "",
    });
  }, [visible, adjacentCrop, form]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSpecies(true);
      try {
        const data = await adjacentCropService.getCropSpecies();
        if (!cancelled) setSpecies(data);
      } catch {
        if (!cancelled) message.error("Error al cargar especies");
      } finally {
        if (!cancelled) setLoadingSpecies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedSpecies) {
      setVarieties([]);
      return;
    }
    (async () => {
      setLoadingVarieties(true);
      try {
        const response = await adjacentCropService.getVarietiesBySpecies(
          selectedSpecies,
        );
        if (!cancelled) setVarieties(response.varieties || []);
      } catch {
        if (!cancelled) {
          message.error("Error al cargar variedades");
          setVarieties([]);
        }
      } finally {
        if (!cancelled) setLoadingVarieties(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSpecies, message]);

  const handleSpeciesChange = (value: string) => {
    setSelectedSpecies(value);
    form.setFieldValue("variety", undefined);
  };

  const submitReview = async () => {
    let values: ReviewFormValues;
    try {
      values = await form.validateFields();
    } catch {
      setStep(0);
      return;
    }
    setSaving(true);
    try {
      await adjacentCropService.createReviewFromAudit({
        audit_id: auditId,
        adjacent_crop_id: adjacentCrop.id,
        species: values.species,
        variety: values.variety || undefined,
        comment: values.comment,
        estimated_surface: values.estimated_surface || 0,
      });
      // The review-from-audit endpoint doesn't accept geometry today
      // — if it changed we still need to persist it. We do a follow-
      // up best-effort PATCH; the review save is the source of
      // truth.
      const initialGeom = JSON.stringify(adjacentCrop.geom ?? null);
      const newGeom = JSON.stringify(geometry ?? null);
      if (newGeom !== initialGeom) {
        try {
          await adjacentCropService.updateAdjacentCrop(adjacentCrop.id, {
            geom: geometry ?? undefined,
          } as any);
        } catch {
          // Surface as a warning — review itself was saved fine.
          message.warning(
            "Revisión guardada, pero no se pudo actualizar la geometría.",
          );
        }
      }
      message.success("Revisión guardada");
      onSuccess();
    } catch (e: any) {
      message.error(e?.message || "Error al guardar la revisión");
    } finally {
      setSaving(false);
    }
  };

  const stepDatos = useMemo(
    () => (
      <Form form={form} layout="vertical">
        <Form.Item
          label="Especie"
          name="species"
          rules={[
            { required: true, message: "Por favor seleccione una especie" },
          ]}
          help="Puede actualizar la especie si ha cambiado"
        >
          <Select
            showSearch
            placeholder="Seleccione una especie"
            loading={loadingSpecies}
            onChange={handleSpeciesChange}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={species.map((s) => ({ value: s.id, label: s.name }))}
          />
        </Form.Item>

        <Form.Item
          label="Variedad (opcional)"
          name="variety"
          help="Puede actualizar la variedad si ha cambiado o se ha identificado"
        >
          <Select
            showSearch
            placeholder="Seleccione una variedad o deje en blanco si es desconocida"
            loading={loadingVarieties}
            disabled={!selectedSpecies}
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={varieties.map((v) => ({ value: v.id, label: v.name }))}
          />
        </Form.Item>

        <Form.Item
          label="Superficie estimada"
          name="estimated_surface"
          rules={[
            {
              required: true,
              message: "Por favor ingrese la superficie estimada",
            },
            { type: "number", min: 0 },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={0.01}
            precision={2}
            placeholder="0.00"
            addonAfter="ha"
          />
        </Form.Item>

        <Form.Item
          label="Comentario de revisión"
          name="comment"
          help="Opcional — usa este campo para apuntar cualquier observación."
        >
          <TextArea
            rows={4}
            placeholder="Observaciones sobre el cultivo adyacente…"
          />
        </Form.Item>
      </Form>
    ),
    [
      form,
      species,
      varieties,
      loadingSpecies,
      loadingVarieties,
      selectedSpecies,
    ],
  );

  const stepMapa = (
    <div>
      <p style={{ color: "#8c8c8c", marginBottom: 12 }}>
        Revisa la geometría del cultivo. Si ha cambiado respecto a la última
        auditoría, redibújala.
      </p>
      {/* Pass `height` explicitly — the editor defaults to 600px,
          which overflows the modal. */}
      <AdjacentCropMapEditor
        geometry={geometry}
        onGeometryChange={(g) => setGeometry(g)}
        height={420}
      />
    </div>
  );

  const steps = [
    { key: "datos", title: "Datos", body: stepDatos },
    { key: "mapa", title: "Mapa", body: stepMapa },
  ];

  // Keep both step bodies mounted at the same time and toggle their
  // visibility with display:none. That way the <Form> never
  // unmounts when the user navigates to the map step, and
  // `form.validateFields()` at submit time still sees every value
  // (otherwise antd drops the field registrations and the POST
  // arrived without `species`).
  // Side-effect: Leaflet maps mount with zero size when hidden, so
  // we fire a resize event whenever the active step changes — the
  // map invalidates its size and re-fetches tiles.
  useEffect(() => {
    const id = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 80);
    return () => window.clearTimeout(id);
  }, [step, visible]);

  const renderSteps = () => (
    <>
      {steps.map((s, idx) => (
        <div
          key={s.key}
          style={{ display: idx === step ? "block" : "none" }}
        >
          {s.body}
        </div>
      ))}
    </>
  );

  return (
    <Modal
      title={`Revisión — ${adjacentCrop.name}`}
      open={visible}
      onCancel={() => (saving ? undefined : onCancel())}
      width={800}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          {step > 0 && (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Anterior
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={async () => {
                if (step === 0) {
                  try {
                    await form.validateFields();
                  } catch {
                    return;
                  }
                }
                setStep((s) => s + 1);
              }}
            >
              Siguiente
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={submitReview}
            >
              Finalizar revisión
            </Button>
          )}
        </Space>
      }
    >
      <Spin spinning={false}>
        <Steps current={step} style={{ marginBottom: 24 }}>
          {steps.map((s) => (
            <Steps.Step key={s.key} title={s.title} />
          ))}
        </Steps>
        {renderSteps()}
      </Spin>
    </Modal>
  );
};

export default AdjacentCropReviewForm;
